type ActiveConversationView = {
  conversationId: string;
  updatedAt: number;
};

const VIEW_TTL_MS = 45_000;
const TAB_ID_KEY = "conversation-active-view-tab-id";
let fallbackTabId: string | null = null;

function getTabId(): string {
  try {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = crypto.randomUUID();
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
    return tabId;
  } catch {
    fallbackTabId ??= crypto.randomUUID();
    return fallbackTabId;
  }
}

function storageKey(userId: string): string {
  return `conversation-active-views:${userId}`;
}

function readViews(userId: string): Record<string, ActiveConversationView> {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const views = raw ? JSON.parse(raw) as Record<string, ActiveConversationView> : {};
    const cutoff = Date.now() - VIEW_TTL_MS;
    for (const [tabId, view] of Object.entries(views)) {
      if (!view || !view.conversationId || view.updatedAt < cutoff) delete views[tabId];
    }
    return views;
  } catch {
    return {};
  }
}

function writeViews(userId: string, views: Record<string, ActiveConversationView>): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(views));
  } catch {
    // Storage is only an optimization for suppressing an alert already visible
    // in another tab. A storage error must never break conversation updates.
  }
}

/**
 * Records the conversation actively visible in this browser tab. The short
 * heartbeat lets other visible tabs of the same agent avoid playing an alert
 * for a chat that is already on screen.
 */
export function setActiveConversationView(userId: string, conversationId: string | null): void {
  if (typeof window === "undefined") return;
  const views = readViews(userId);
  const tabId = getTabId();
  if (conversationId) {
    views[tabId] = { conversationId, updatedAt: Date.now() };
  } else {
    delete views[tabId];
  }
  writeViews(userId, views);
}

export function isConversationActivelyViewed(userId: string, conversationId: string): boolean {
  if (typeof window === "undefined") return false;
  // A background browser tab is no longer the conversation the agent is
  // actively viewing. This also prevents a stale cross-tab marker from
  // suppressing the sound after the agent switches to another browser tab.
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return false;
  return Object.values(readViews(userId)).some((view) => view.conversationId === conversationId);
}